// SelfHeal API client — attaches Clerk JWT to every request

const API_BASE = import.meta.env.VITE_API_URL || ''

// Active Clerk org, published by OrganizationGate. Used to reject a __session
// cookie whose org claim still points at the previously selected workspace.
let activeOrgId: string | null = null

export function setActiveOrgId(id: string | null): void {
  activeOrgId = id
}

// Same claim shapes the backend accepts (app/auth/jwt_auth.py: organization / org_id / o.id).
function tokenOrgId(token: string): string | null {
  try {
    const body = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const claims = JSON.parse(atob(body)) as {
      organization?: string
      org_id?: string
      o?: { id?: string }
    }
    return claims.organization ?? claims.org_id ?? claims.o?.id ?? null
  } catch {
    return null
  }
}

// Reads the Clerk __session cookie which is kept fresh by Clerk and is longer-lived
// than the default getToken() cached value, avoiding 401s on short-lived tokens.
// After an organization switch the cookie lags by a beat, so a mismatched (or
// undecodable) org claim makes callers fall back to Clerk's getToken().
export function getSessionCookieToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)__session=([^;]*)/)
  if (!match) return null
  const token = decodeURIComponent(match[1])
  if (activeOrgId && tokenOrgId(token) !== activeOrgId) return null
  return token
}

export type Investigation = {
  id: string
  alert_name: string
  alert_source?: string
  severity: string
  status: 'running' | 'complete' | 'failed' | 'stale'
  created_at: string
  updated_at: string
  org_id: string
  summary?: string
  /** Number of investigation steps the agent has run so far (from the backend list endpoint). */
  step_count?: number
  /** 'pending'/'running' when a remediation attempt (e.g. a retry) is in flight, else null. */
  remediation_status?: 'pending' | 'running' | null
}

export type RemediationStatus =
  | 'idle'
  | 'planning'
  | 'blocked'
  | 'executing'
  | 'ready'
  | 'failed'
  | 'rca_mismatch'

export type RemediationArtifact = {
  operation?: string
  url?: string
  branch?: string
  repo?: string
  files_changed?: number
  extra?: {
    branch?: string
    patches_applied?: number
    investigation_id?: string
    unified_diff?: string
    [key: string]: unknown
  }
}

export type RemediationPlanEntry = {
  id?: string
  fix_type?: string
  status?: string
  proposed_action?: string
  confidence?: number
  requires_approval?: boolean
}

export type RemediationMeta = {
  execution_mode?: 'auto' | 'approval'
  sandbox_run_id?: string
  github_installation_id?: string
  last_error?: string
  error_code?: string
  /** rca_mismatch: the agent's specific, code-grounded explanation of why no code
   *  fix applies and where the real cause lies. */
  rca_mismatch_explanation?: string
}

export type RemediationSummary = {
  /** Canonical UI state from API (HEA-214). */
  state: RemediationStatus
  /** Legacy alias; mirrors `state` when present. */
  status?: RemediationStatus
  plan: RemediationPlanEntry[]
  artifacts: RemediationArtifact[]
  meta: RemediationMeta
  poll_active?: boolean
}

export function remediationStatus(
  remediation: RemediationSummary | null | undefined,
): RemediationStatus {
  if (!remediation) return 'idle'
  return remediation.state ?? remediation.status ?? 'idle'
}

export type GraphNodeStatus = 'culprit' | 'impacted' | 'contributing' | 'healthy'
export type GraphNodeType = 'service' | 'pod' | 'signal' | 'cause' | 'alert' | 'remediation'

export type RcaGraphNode = {
  id: string
  label: string
  type: GraphNodeType
  status: GraphNodeStatus
  detail: string
}

export type RcaGraphEdge = {
  source: string
  target: string
  label: string
}

export type RcaSubGraph = {
  summary: string
  nodes: RcaGraphNode[]
  edges: RcaGraphEdge[]
}

export type RcaGraph = {
  impact: RcaSubGraph
  root_cause: RcaSubGraph
}

export type InvestigationDetail = Investigation & {
  rca: {
    root_cause: string
    contributing_factors: string[]
    recommendations: string[]
    evidence: Evidence[]
    findings?: Finding[]
    graph?: RcaGraph | null
    // Set when the remediation agent refuted the original RCA after reading the
    // code; root_cause holds the corrected finding and original_root_cause the
    // superseded text.
    corrected?: boolean
    original_root_cause?: string
  } | null
  timeline: TimelineEvent[]
  alert_payload: Record<string, unknown>
  remediation?: RemediationSummary | null
}

export type Evidence = {
  tool: string
  step_order: number
  result: Record<string, unknown> | string
  timestamp: string
  query?: string
  findings?: string[]
  sources?: string[]
}

export type Finding = {
  claim: string
  validated: boolean
  evidence_labels: string[]
}

export type TimelineEvent = {
  timestamp: string
  event: string
  source: string
}

export type ListInvestigationsResponse = {
  items: Investigation[]
  total: number
  page: number
  page_size: number
}

export type ApiErrorPayload = {
  status: number
  code?: string
  detail?: string
}

export class ApiRequestError extends Error {
  readonly status: number
  readonly code?: string
  readonly detail?: string

  constructor(payload: ApiErrorPayload) {
    const detail = payload.detail ?? 'Request failed'
    super(`API ${payload.status}: ${detail}`)
    this.name = 'ApiRequestError'
    this.status = payload.status
    this.code = payload.code
    this.detail = payload.detail
  }
}

async function apiFetch<T>(path: string, token: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    },
  })

  if (!res.ok) {
    const body = await res.text()
    let code: string | undefined
    let detail: string | undefined
    try {
      const parsed = JSON.parse(body) as { code?: string; detail?: string }
      code = parsed.code
      detail = parsed.detail
    } catch {
      detail = body || undefined
    }
    throw new ApiRequestError({ status: res.status, code, detail })
  }

  return res.json() as Promise<T>
}

export type ChecklistStep = {
  step_key: string
  label: string
  description: string
  order: number
  completed: boolean
  completed_at: string | null
}

export type ChecklistResponse = {
  steps: ChecklistStep[]
  total: number
  completed: number
  progress_pct: number
}

export type StepUpdateResponse = {
  step_key: string
  completed: boolean
  completed_at: string | null
}

export type HealthStatus = 'ok' | 'auth_error' | 'error' | 'missing'

export type CredentialHealthResponse = {
  integrations: Record<string, HealthStatus>
}

export type SingleHealthResponse = {
  integration: string
  status: HealthStatus
  message: string
}

export type UpsertCredentialsResponse = {
  integration: string
  keys_stored: string[]
}

export type IntegrationField = {
  key: string
  label: string
  secret: boolean
  placeholder?: string | null
}

export type IntegrationCatalogEntry = {
  id: string
  display_name: string
  connection_method: string
  category: string
  docs_path: string
  fields: IntegrationField[]
}

export type CliCatalogResponse = {
  cli: IntegrationCatalogEntry[]
  total: number
}

export type SentryWebhookInfo = {
  tenant_id: string
  webhook_url: string
  webhook_secret_configured: boolean
  api_credentials_configured: boolean
}

export type SentryProjectRepoMappingRow = {
  project_slug: string
  github_repo: string
}

export type SentryProjectRepoMapResponse = {
  mappings: SentryProjectRepoMappingRow[]
  github_repos: string[]
  github_connected: boolean
  sentry_api_configured: boolean
  sentry_projects: { slug: string; name: string }[]
}

export type VercelWebhookInfo = {
  tenant_id: string
  webhook_url: string
  webhook_secret_configured: boolean
  api_credentials_configured: boolean
}

export const api = {
  listInvestigations(
    token: string,
    params: { page?: number; page_size?: number; status?: string } = {},
  ): Promise<ListInvestigationsResponse> {
    const qs = new URLSearchParams()
    if (params.page) qs.set('page', String(params.page))
    if (params.page_size) qs.set('page_size', String(params.page_size))
    if (params.status) qs.set('status', params.status)
    return apiFetch<ListInvestigationsResponse>(
      `/api/v1/investigations?${qs.toString()}`,
      token,
    )
  },

  getInvestigation(token: string, id: string): Promise<InvestigationDetail> {
    return apiFetch<InvestigationDetail>(`/api/v1/investigations/${id}`, token)
  },

  retryRemediation(token: string, investigationId: string): Promise<{ ok: boolean }> {
    return apiFetch<{ ok: boolean }>(
      `/api/v1/investigations/${investigationId}/remediation/retry`,
      token,
      { method: 'POST' },
    )
  },

  getChecklist(token: string): Promise<ChecklistResponse> {
    return apiFetch<ChecklistResponse>('/api/v1/onboarding/checklist', token)
  },

  completeStep(token: string, stepKey: string): Promise<StepUpdateResponse> {
    return apiFetch<StepUpdateResponse>(
      `/api/v1/onboarding/checklist/${stepKey}/complete`,
      token,
      { method: 'POST' },
    )
  },

  resetStep(token: string, stepKey: string): Promise<StepUpdateResponse> {
    return apiFetch<StepUpdateResponse>(
      `/api/v1/onboarding/checklist/${stepKey}/complete`,
      token,
      { method: 'DELETE' },
    )
  },

  getCatalog(token: string): Promise<IntegrationCatalogEntry[]> {
    return apiFetch<IntegrationCatalogEntry[]>('/api/v1/credentials/catalog', token)
  },

  getCliCatalog(token: string): Promise<CliCatalogResponse> {
    return apiFetch<CliCatalogResponse>('/api/v1/credentials/catalog/cli', token)
  },

  getCredentialsHealth(token: string): Promise<CredentialHealthResponse> {
    return apiFetch<CredentialHealthResponse>('/api/v1/credentials/health', token)
  },

  getSentryWebhook(token: string): Promise<SentryWebhookInfo> {
    return apiFetch<SentryWebhookInfo>('/api/v1/credentials/sentry/webhook', token)
  },

  getSentryProjectRepoMap(token: string): Promise<SentryProjectRepoMapResponse> {
    return apiFetch<SentryProjectRepoMapResponse>(
      '/api/v1/credentials/sentry/project-repo-map',
      token,
    )
  },

  putSentryProjectRepoMap(
    token: string,
    body: { mappings: SentryProjectRepoMappingRow[] },
  ): Promise<SentryProjectRepoMapResponse> {
    return apiFetch<SentryProjectRepoMapResponse>(
      '/api/v1/credentials/sentry/project-repo-map',
      token,
      { method: 'PUT', body: JSON.stringify(body) },
    )
  },

  getVercelWebhook(token: string): Promise<VercelWebhookInfo> {
    return apiFetch<VercelWebhookInfo>('/api/v1/credentials/vercel/webhook', token)
  },

  verifyIntegrationHealth(token: string, integration: string): Promise<SingleHealthResponse> {
    return apiFetch<SingleHealthResponse>(`/api/v1/credentials/${integration}/health`, token)
  },

  upsertCredentials(
    token: string,
    integration: string,
    credentials: Record<string, string>,
  ): Promise<UpsertCredentialsResponse> {
    return apiFetch<UpsertCredentialsResponse>(`/api/v1/credentials/${integration}`, token, {
      method: 'POST',
      body: JSON.stringify({ credentials }),
    })
  },

  deleteCredentials(token: string, integration: string): Promise<void> {
    return apiFetch<void>(`/api/v1/credentials/${integration}`, token, { method: 'DELETE' })
  },

  // ── Notifications ───────────────────────────────────────────────
  listNotifications(token: string): Promise<ListNotificationsResponse> {
    return apiFetch<ListNotificationsResponse>('/api/v1/notifications', token)
  },

  markNotificationRead(token: string, id: string): Promise<void> {
    return apiFetch<void>(`/api/v1/notifications/${id}/read`, token, { method: 'POST' })
  },

  markAllNotificationsRead(token: string): Promise<void> {
    return apiFetch<void>('/api/v1/notifications/read-all', token, { method: 'POST' })
  },

  // ── Org notification settings ──────────────────────────────────
  getNotificationSettings(token: string): Promise<OrgNotificationSettings> {
    return apiFetch<OrgNotificationSettings>('/api/v1/org/notification-settings', token)
  },

  saveNotificationSettings(token: string, settings: OrgNotificationSettings): Promise<OrgNotificationSettings> {
    return apiFetch<OrgNotificationSettings>('/api/v1/org/notification-settings', token, {
      method: 'PUT',
      body: JSON.stringify(settings),
    })
  },

  // ── Org remediation settings ───────────────────────────────────
  getRemediationSettings(token: string): Promise<OrgRemediationSettings> {
    return apiFetch<OrgRemediationSettings>('/api/v1/org/remediation-context', token)
  },

  saveRemediationSettings(token: string, settings: OrgRemediationSettings): Promise<OrgRemediationSettings> {
    return apiFetch<OrgRemediationSettings>('/api/v1/org/remediation-context', token, {
      method: 'PUT',
      body: JSON.stringify(settings),
    })
  },

  // ── Incident forecasting ───────────────────────────────────────
  getForecastConfig(token: string): Promise<ForecastConfigResponse> {
    return apiFetch<ForecastConfigResponse>('/api/v1/forecast/config', token)
  },

  saveForecastConfig(token: string, config: ForecastConfig): Promise<ForecastConfigResponse> {
    return apiFetch<ForecastConfigResponse>('/api/v1/forecast/config', token, {
      method: 'PUT',
      body: JSON.stringify(config),
    })
  },

  async deleteForecastConfig(token: string): Promise<void> {
    const res = await fetch(`${API_BASE}/api/v1/forecast/config`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) throw new ApiRequestError({ status: res.status, detail: await res.text() })
  },

  issueForecastAgentToken(token: string): Promise<{ token: string }> {
    return apiFetch<{ token: string }>('/api/v1/forecast/agent-token', token, { method: 'POST' })
  },

  getForecastStatus(token: string): Promise<ForecastStatus> {
    return apiFetch<ForecastStatus>('/api/v1/forecast/status', token)
  },

  getGithubStatus(token: string): Promise<{
    installed: boolean
    installation_count: number
    repo_count: number
    error_code: string | null
  }> {
    return apiFetch(`/api/v1/integrations/github/status`, token)
  },

  getGithubInstallUrl(
    token: string,
    redirectUri: string,
    investigationId?: string,
  ): Promise<{ install_url: string; state: string }> {
    const qs = new URLSearchParams({ redirect_uri: redirectUri })
    if (investigationId) qs.set('investigation_id', investigationId)
    return apiFetch<{ install_url: string; state: string }>(
      `/api/v1/integrations/github/install-url?${qs.toString()}`,
      token,
    )
  },

  disconnectGithub(token: string): Promise<void> {
    return apiFetch<void>('/api/v1/integrations/github/disconnect', token, { method: 'DELETE' })
  },

  getBitbucketStatus(token: string): Promise<{
    connected: boolean
    workspace: string | null
    error_code: string | null
  }> {
    return apiFetch(`/api/v1/integrations/bitbucket/status`, token)
  },

  getBitbucketConnectUrl(
    token: string,
    redirectUri: string,
  ): Promise<{ connect_url: string; state: string }> {
    const qs = new URLSearchParams({ redirect_uri: redirectUri })
    return apiFetch<{ connect_url: string; state: string }>(
      `/api/v1/integrations/bitbucket/connect-url?${qs.toString()}`,
      token,
    )
  },

  disconnectBitbucket(token: string): Promise<void> {
    return apiFetch<void>('/api/v1/integrations/bitbucket/disconnect', token, { method: 'DELETE' })
  },

  async getSlackStatus(): Promise<{ oauth_configured: boolean }> {
    const res = await fetch(`${API_BASE}/api/v1/integrations/slack/status`)
    if (!res.ok) return { oauth_configured: false }
    return res.json() as Promise<{ oauth_configured: boolean }>
  },

  getSlackInstallUrl(token: string, redirectUri: string): Promise<{ install_url: string; state: string }> {
    const qs = new URLSearchParams({ redirect_uri: redirectUri })
    return apiFetch<{ install_url: string; state: string }>(
      `/api/v1/integrations/slack/install-url?${qs.toString()}`,
      token,
    )
  },

  disconnectSlack(token: string): Promise<void> {
    return apiFetch<void>('/api/v1/integrations/slack/disconnect', token, { method: 'DELETE' })
  },

  // ── BYOK (Bring Your Own Key) ──────────────────────────────────
  getByokConfig(token: string): Promise<ByokConfigResponse> {
    return apiFetch<ByokConfigResponse>('/api/v1/byok', token)
  },

  saveByokConfig(token: string, payload: SaveByokRequest): Promise<ByokConfigResponse> {
    return apiFetch<ByokConfigResponse>('/api/v1/byok', token, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  },

  validateByokKey(token: string): Promise<ByokValidationResponse> {
    return apiFetch<ByokValidationResponse>('/api/v1/byok/validate', token, { method: 'POST' })
  },

  updateByokToggles(token: string, toggles: ByokTogglesRequest): Promise<ByokConfigResponse> {
    return apiFetch<ByokConfigResponse>('/api/v1/byok/toggles', token, {
      method: 'PATCH',
      body: JSON.stringify(toggles),
    })
  },

  revokeByokConfig(token: string): Promise<void> {
    return apiFetch<void>('/api/v1/byok', token, { method: 'DELETE' })
  },
}

// ── BYOK types ─────────────────────────────────────────────────────

export type ByokKeyStatus = 'pending' | 'validated' | 'invalid' | 'revoked'

export type ByokConfigResponse = {
  feature_enabled: boolean
  configured: boolean
  provider: string | null
  base_url: string | null
  rca_model: string | null
  code_fix_model: string | null
  key_status: ByokKeyStatus | null
  key_fingerprint: string | null
  rca_enabled: boolean
  code_fix_enabled: boolean
  fallback_enabled: boolean
  updated_at: string | null
  updated_by_sub: string | null
  supported_providers: string[]
  provider_models: Record<string, string[]>
}

export type SaveByokRequest = {
  provider: string
  api_key: string
  base_url?: string | null
  rca_model?: string | null
  code_fix_model?: string | null
  fallback_enabled?: boolean
}

export type ByokValidationResponse = {
  success: boolean
  message: string
  key_status: ByokKeyStatus
}

export type ByokTogglesRequest = {
  rca_enabled?: boolean | null
  code_fix_enabled?: boolean | null
  fallback_enabled?: boolean | null
}

// ── Notification types ─────────────────────────────────────────────

export type NotificationKind = 'rca_ready' | 'patch_ready' | 'pr_ready' | 'rca_mismatch'

export type AppNotification = {
  id: string
  kind: NotificationKind
  read: boolean
  created_at: string
  investigation_id: string
  alert_name: string
  severity: string
  /** One-line root-cause summary (rca_ready only) */
  summary?: string
  /** PR URL (pr_ready only) */
  pr_url?: string
  /** PR title (pr_ready only) */
  pr_title?: string
}

export type ListNotificationsResponse = {
  items: AppNotification[]
  unread_count: number
}

export type OrgNotificationSettings = {
  slack_connected: boolean
  slack_default_channel: string
  email_recipients: string
  notify_patch_pr: boolean
  quiet_hours_enabled: boolean
  quiet_hours_start: string
  quiet_hours_end: string
}

export type OrgRemediationSettings = {
  stack_description: string
}

// ── Incident forecasting ─────────────────────────────────────────

/** One connection in a tenant's forecast config: ``type`` picks the adapter, the rest are its params. */
export type ForecastSource = { type: string; name?: string; via?: 'healops' | 'agent' } & Record<string, unknown>

export type ForecastConfig = {
  sources: ForecastSource[]
  backfill_days?: number
  label_min_impact?: number
  label_mad_k?: number
}

/** JSON schema of one adapter's params, as pydantic emits it. */
export type ForecastParamsSchema = {
  properties?: Record<
    string,
    { type?: string; title?: string; description?: string; default?: unknown; items?: { type?: string } }
  >
  required?: string[]
}

export type ForecastSourceType = {
  type: string
  description: string
  history: boolean
  params_schema: ForecastParamsSchema
}

export type ForecastConfigResponse = {
  config: ForecastConfig | null
  defaults: Omit<ForecastConfig, 'sources'>
  source_types: ForecastSourceType[]
}

export type ForecastModelRun = {
  id: string
  created_at: string
  active: boolean
  metrics: Record<string, unknown>
}

export type ForecastStatus = {
  probability: number | null
  flagged: boolean | null
  scored_at: string | null
  runs: ForecastModelRun[]
  sources: { source: string; last_bucket: string | null; points_24h: number }[]
}
